import { Card, Col, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBan,
  faBoxesStacked,
  faClipboardList,
  faIndustry,
  faHandshake,
  faUsers
} from '@fortawesome/free-solid-svg-icons';
import { formatLength, exactLength } from '../../../helpers/formatLength';

/**
 * Six numbers that answer "how are we doing" without a click.
 *
 * The middle four are one sentence read left to right: this much was asked for,
 * this much we made, this much came off the shelf, this much is still only a
 * promise. Splitting made-from-stock out of "delivered" matters here more than
 * anywhere — cable pulled from stock is revenue with no machine time behind it,
 * and lumping the two together hides how much the factory actually ran.
 */

const pct = (part, whole) =>
  whole > 0 ? `${Math.round((part / whole) * 100)}%` : '—';

// Phoenix generates the -100 tint for the five semantic colours and no others —
// `bg-secondary-100` is not a class, and a card asking for it gets a colourless
// circle rather than an error. `bg-200` is the theme's own neutral.
const tint = color => (color === 'secondary' ? 'bg-200' : `bg-${color}-100`);

const Kpi = ({ icon, color, value, exact, label, sub }) => (
  <Card className="h-100">
    <Card.Body className="d-flex align-items-center gap-3 py-3">
      <div
        className={`d-flex align-items-center justify-content-center rounded-circle ${tint(
          color
        )} flex-shrink-0`}
        style={{ width: 44, height: 44 }}
      >
        <FontAwesomeIcon icon={icon} className={`text-${color}`} />
      </div>
      <div className="min-w-0">
        <div className="fs-6 fw-bolder lh-1" title={exact}>
          {value}
        </div>
        <div className="text-700 fs-10 fw-semibold">{label}</div>
        {sub && <div className="text-body-tertiary fs-10">{sub}</div>}
      </div>
    </Card.Body>
  </Card>
);

const OrdersAnalyticsKpis = ({ kpis }) => {
  const k = kpis || {};

  const cards = [
    {
      icon: faClipboardList,
      color: 'secondary',
      value: (k.orders || 0).toLocaleString(),
      label: 'Orders',
      sub: `${k.open || 0} open · ${k.archived || 0} delivered · ${
        k.deleted || 0
      } deleted`
    },
    {
      icon: faHandshake,
      color: 'primary',
      value: formatLength(k.ordered),
      exact: exactLength(k.ordered),
      label: 'Ordered',
      sub: `${(k.lines || 0).toLocaleString()} line${
        k.lines === 1 ? '' : 's'
      }`
    },
    {
      icon: faIndustry,
      color: 'success',
      value: formatLength(k.produced),
      exact: exactLength(k.produced),
      label: 'Made',
      sub: `${pct(k.produced, k.ordered)} of what was ordered`
    },
    {
      icon: faBoxesStacked,
      color: 'info',
      value: formatLength(k.stock),
      exact: exactLength(k.stock),
      label: 'From stock',
      sub: 'delivered without production'
    },
    {
      icon: faUsers,
      color: 'warning',
      value: formatLength(k.promised),
      exact: exactLength(k.promised),
      label: 'Promised, not made',
      sub: `${formatLength(k.stillOpen)} not even promised`
    },
    {
      icon: faBan,
      color: 'danger',
      value: formatLength(k.cancelled),
      exact: exactLength(k.cancelled),
      label: 'Cancelled',
      sub: `${k.clients || 0} customer${k.clients === 1 ? '' : 's'} in total`
    }
  ];

  return (
    <Row className="g-3 mb-4">
      {cards.map(c => (
        <Col key={c.label} xs={12} sm={6} lg={4} xxl={2}>
          <Kpi {...c} />
        </Col>
      ))}
    </Row>
  );
};

export default OrdersAnalyticsKpis;
